/**
 * Recency colours for /insights (SALES_INSIGHTS_PLAN.md §5b, D14–D16):
 * current ≤ 91 days (green), warm ≤ 365 (amber), cold beyond (grey). The same
 * bands colour competitor customers and the IR/PR firm line, so a firm switch
 * reads as a grey firm beside a green one. The API key for the first band
 * stays "hot"; operator 2026-10-05 relabelled it "current" and moved it off
 * red, which read as an alarm.
 */
import { Link } from 'react-router-dom';
import type { CompetitorSignal, FirmLink, RecencyBand } from '../../lib/api/insights.js';

export const BAND_LABEL: Record<RecencyBand, string> = { hot: 'current', warm: 'warm', cold: 'cold' };
const CHIP: Record<RecencyBand, string> = {
  hot: 'bg-success-50 text-success-700',
  warm: 'bg-warning-50 text-warning-700',
  cold: 'bg-grey-100 text-grey-600',
};
const TEXT: Record<RecencyBand, string> = {
  hot: 'text-success-700',
  warm: 'text-warning-700',
  cold: 'text-grey-500',
};

function fmtDay(d: string | null): string {
  return d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

/** Competitor customers as coloured tags, e.g. "Discovery Alert · warm". */
export function CompetitorTags({ items }: { items?: CompetitorSignal[] }) {
  if (!items?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {items.map((s) => {
        const approx = s.date_basis === 'observed';
        const title = `${s.competitor}: ${approx ? 'observed by' : 'last paid coverage'} ${fmtDay(s.last_seen)}`
          + (s.first_seen ? ` (since ${fmtDay(s.first_seen)})` : '')
          + (s.pieces ? ` · ${s.pieces} pieces` : '');
        return (
          <span key={s.competitor} title={title}
            className={`inline-block rounded-full px-2 py-0.5 text-[11px] leading-4 ${s.band ? CHIP[s.band] : 'bg-grey-100 text-grey-700'}`}>
            {s.competitor} · {approx ? '~' : ''}{s.band ? BAND_LABEL[s.band] : 'undated'}
          </span>
        );
      })}
    </span>
  );
}

/** IR/PR firms, newest first, each name coloured by when we last saw it act. */
export function FirmLinks({ links, empty = 'none observed' }: { links?: FirmLink[]; empty?: string }) {
  if (!links?.length) return <span className="text-grey-400">{empty}</span>;
  return (
    <span>
      {links.map((l, i) => (
        <span key={l.firm_id}>
          {i > 0 && ', '}
          <Link to={`/insights/firm/${l.firm_id}`} onClick={(e) => e.stopPropagation()}
            title={`Seen ${fmtDay(l.first_seen)} → ${fmtDay(l.last_seen)}`}
            className={`hover:underline ${l.band ? TEXT[l.band] : 'text-grey-600'}`}>
            {l.name}
          </Link>
        </span>
      ))}
    </span>
  );
}

/** Legend plus the DA list's own handling rule (D16). */
export function RecencyLegend() {
  return (
    <p className="text-[11px] text-grey-500">
      <span className="text-success-700">● current</span> under 3 months ·{' '}
      <span className="text-warning-700">● warm</span> 3–12 months ·{' '}
      <span className="text-grey-500">● cold</span> over 12 months. Competitor tags with ~ are dated by when the list
      was compiled. Competitor data is an internal prospecting reference: outreach stands on our own merits and never
      cites where this came from.
    </p>
  );
}
